import { createBrowserRouter, type LoaderFunction } from "react-router-dom";
import RootLayout from "./layouts/RootLayout";
import ErrorBoundary from "./components/ErrorBoundary";

/**
 * Loader-based authentication guard.
 * For now, we defer to ProtectedRoute component-level checks
 * since MSAL context isn't available in loaders.
 * This keeps the route definition clean and allows future migration
 * to full loader-based auth when MSAL supports it.
 */
const requireAuth: LoaderFunction = async () => {
  // Return null to proceed - ProtectedRoute will handle MSAL checks
  // In a future refactor, you could check MSAL token validity here
  return null;
};

/**
 * Hydration fallback component.
 * Displayed during initial hydration when using SSR or React 19 features.
 */
function HydrateFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-background-light dark:bg-surface-background-dark">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent"></div>
        <p className="mt-4 text-sm text-foreground-muted-light dark:text-foreground-muted-dark">Loading…</p>
      </div>
    </div>
  );
}

/**
 * React Router 7 configuration with loader-based route protection,
 * lazy loading, error boundaries, and prefetching support.
 *
 * Key features:
 * - Route-level code splitting via `lazy`
 * - Automatic Suspense per route (no global wrapper needed)
 * - Error boundaries for graceful error handling
 * - ScrollRestoration in RootLayout
 * - Loader-based auth guards (delegating to ProtectedRoute for MSAL)
 * - HydrateFallback for initial hydration
 * - HMR-compatible with proper disposal
 */
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    HydrateFallback,
    children: [
      {
        path: "login",
        lazy: async () => {
          const { default: Component } = await import("./pages/LoginRedirectPage");
          return { Component };
        },
      },
      {
        path: "login/processing",
        lazy: async () => {
          const { default: Component } = await import("./pages/LoginProcessingPage");
          return { Component };
        },
      },
      {
        path: "logout/callback",
        lazy: async () => {
          const { default: Component } = await import("./pages/SignOutRedirectPage");
          return { Component };
        },
      },
      {
        path: "admin-consent",
        lazy: async () => {
          const { default: Component } = await import("./pages/AdminConsentGuidancePage");
          return { Component };
        },
      },
      {
        index: true,
        lazy: async () => {
          const { default: LandingPage } = await import("./pages/LandingPage");
          return { Component: LandingPage };
        },
      },
      {
        path: "extensions",
        loader: requireAuth,
        lazy: async () => {
          const { default: ExtensionsPage } = await import("./pages/ExtensionsPage");
          return { Component: ExtensionsPage };
        },
      },
      {
        path: "extensions/metrics",
        loader: requireAuth,
        lazy: async () => {
          const { default: ExtensionsDashboardPage } = await import("./pages/extensions/metrics/ExtensionsDashboardPage");
          return { Component: ExtensionsDashboardPage };
        },
      },
      {
        path: "users",
        loader: requireAuth,
        lazy: async () => {
          const { default: UsersPage } = await import("./pages/UsersPage");
          return { Component: UsersPage };
        },
      },
      {
        path: "organizations",
        loader: requireAuth,
        lazy: async () => {
          const { default: OrganizationsPage } = await import("./pages/OrganizationsPage");
          return { Component: OrganizationsPage };
        },
      },
      {
        path: "organizations/:id",
        loader: requireAuth,
        lazy: async () => {
          const { default: OrganizationDetailsPage } = await import("./pages/OrganizationDetailsPage");
          return { Component: OrganizationDetailsPage };
        },
      },
      {
        path: "apps",
        loader: requireAuth,
        lazy: async () => {
          const { default: AppsPage } = await import("./pages/AppsPage");
          return { Component: AppsPage };
        },
      },
      {
        path: "licenses",
        loader: requireAuth,
        lazy: async () => {
          const { LicenseManagementPage } = await import("./features/licenses/LicenseManagementPage.container");
          return { Component: LicenseManagementPage };
        },
      },
      {
        path: "integrations",
        loader: requireAuth,
        lazy: async () => {
          const { default: IntegrationsPage } = await import("./pages/IntegrationsPage");
          return { Component: IntegrationsPage };
        },
      },
      {
        path: "statuses",
        loader: requireAuth,
        lazy: async () => {
          const { default: StatusesPage } = await import("./pages/StatusesPage");
          return { Component: StatusesPage };
        },
      },
      {
        path: "notifications",
        loader: requireAuth,
        lazy: async () => {
          const { default: NotificationsPage } = await import("./pages/NotificationsPage");
          return { Component: NotificationsPage };
        },
      },
      {
        path: "profile",
        loader: requireAuth,
        lazy: async () => {
          const { default: ProfilePage } = await import("./pages/ProfilePage");
          return { Component: ProfilePage };
        },
      },
      {
        path: "ai-credits",
        loader: requireAuth,
        lazy: async () => {
          const { MyCreditsPage } = await import("./features/ai-credits/personal/MyCreditsPage.container");
          return { Component: MyCreditsPage };
        },
      },
      {
        path: "ai-settings",
        loader: requireAuth,
        lazy: async () => {
          const { AiSettingsPage } = await import("./features/ai-credits/settings/AiSettingsPage.container");
          return { Component: AiSettingsPage };
        },
      },
      {
        path: "extensions/:id",
        loader: requireAuth,
        lazy: async () => {
          const { default: ExtensionDetailsPage } = await import("./pages/ExtensionDetailsPage");
          return { Component: ExtensionDetailsPage };
        },
      },
      {
        path: "getting-started",
        lazy: async () => {
          const { default: GettingStarted } = await import("./pages/GettingStarted");
          return { Component: GettingStarted };
        },
      },
      {
        path: "ai-privacy-security",
        lazy: async () => {
          const { default: AiPrivacySecurityPage } = await import("./pages/AiPrivacySecurityPage");
          return { Component: AiPrivacySecurityPage };
        },
      },
      {
        path: "ai-provider-setup",
        lazy: async () => {
          const { default: AiProviderSetupPage } = await import("./pages/AiProviderSetupPage");
          return { Component: AiProviderSetupPage };
        },
      },
      {
        path: "machine-policies",
        lazy: async () => {
          const { default: MachinePoliciesPage } = await import("./pages/MachinePoliciesPage");
          return { Component: MachinePoliciesPage };
        },
      },
      {
        path: "*",
        lazy: async () => {
          const { default: Component } = await import("./pages/NotFoundPage");
          return { Component };
        },
      },
    ],
  },
]);

/**
 * HMR support: Dispose of router instance when module is replaced
 * This prevents memory leaks and ensures proper cleanup during development
 */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    router.dispose();
  });
}

export { router };
